import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import { resolveHeaderSegments } from '../../../../lib/rteTableHelper';

const HEADING_FONT_SIZES = { 1: 32, 2: 24, 3: 18.72, 4: 16, 5: 13.28, 6: 10.72 };

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTH = styled.div`
  box-sizing: border-box;
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  font-size: ${(props) => props.fontSize || 14}px;
  color: ${(props) => props.fontColor || 'inherit'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || '600'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  padding: ${(props) => props.padding || 8}px;
  border-right: ${(props) => props.border};
  border-bottom: ${(props) => props.border};
  flex: 1 1 0;
  min-width: 80px;
  ${(props) => props.textOverflow === 'ellipsis'
    ? 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
    : 'overflow-wrap: break-word; white-space: normal;'}
`;

const StyledNumberTH = styled.div`
  box-sizing: border-box;
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  font-size: ${(props) => props.fontSize || 14}px;
  font-weight: 600;
  text-align: center;
  padding: ${(props) => props.padding || 8}px;
  border-right: ${(props) => props.border};
  border-bottom: ${(props) => props.border};
  max-width: 48px;
  min-width: 48px;
`;

const StyledMenuTH = styled.div`
  box-sizing: border-box;
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  border-right: ${(props) => props.border};
  border-bottom: ${(props) => props.border};
  max-width: 20px;
  min-width: 20px;
`;


class TableFieldRteHeader extends PureComponent {

  static propTypes = {
    id: PropTypes.string,
    rteColumns: PropTypes.array,
    tableColumns: PropTypes.array,
    fieldMode: PropTypes.string,
    enableNumber: PropTypes.bool,
    enableMenu: PropTypes.bool,
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,

    loadCompletedId: PropTypes.string,
    onLoadCompleted: PropTypes.func,
  }

  constructor(props) {
    super(props);
    this.state = { rowRef: null };
    this._lastReportedHeight = null;
  }

  componentDidUpdate() {
    const { onLoadCompleted, loadCompletedId } = this.props;
    const { rowRef } = this.state;
    if (onLoadCompleted && rowRef) {
      const height = rowRef.offsetHeight;
      if (this._lastReportedHeight === height) return;
      this._lastReportedHeight = height;
      onLoadCompleted(loadCompletedId || 'header', { height });
    }
  }

  render() {

    const {
      id,
      rteColumns,
      tableColumns,
      fieldMode,
      enableNumber,
      enableMenu,

      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      backgroundColor,
      padding,
      borderWidth,
      borderColor,
    } = this.props;

    const border = `${borderWidth || 1}px solid ${borderColor || '#e0e0e0'}`;

    const columnNodes = (rteColumns || []).map((column, index) => {
      const segments = resolveHeaderSegments(column.title, tableColumns);
      return (
        <StyledTH
          key={column._id}
          data-testid={`${id}-${column._id}-rte-header-th`}
          {...{
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            backgroundColor,
            padding,
            border,
          }}
        >
          {segments.map((seg, i) => {
            const segStyle = {};
            if (seg.textStyleColor) segStyle.color = seg.textStyleColor;
            if (seg.bold || seg.headingLevel) segStyle.fontWeight = 'bold';
            if (seg.italic) segStyle.fontStyle = 'italic';
            if (seg.underline && seg.strike) segStyle.textDecoration = 'underline line-through';
            else if (seg.underline) segStyle.textDecoration = 'underline';
            else if (seg.strike) segStyle.textDecoration = 'line-through';
            if (seg.headingLevel) segStyle.fontSize = `${HEADING_FONT_SIZES[seg.headingLevel] || 16}px`;
            return (
              <span key={i} style={Object.keys(segStyle).length > 0 ? segStyle : undefined}>
                {seg.text}
              </span>
            );
          })}
        </StyledTH>
      );
    });

    return (
      <StyledRow
        ref={(e) => {
          const { rowRef } = this.state;
          if (!rowRef) this.setState({ rowRef: e });
        }}
      >
        {enableNumber ? (
          <StyledNumberTH backgroundColor={backgroundColor} border={border} fontSize={fontSize} padding={padding}>
            #
          </StyledNumberTH>
        ) : null}
        {enableMenu ? <StyledMenuTH backgroundColor={backgroundColor} border={border} /> : null}
        {columnNodes}
      </StyledRow>
    );

  }

}

const arePropsEqual = (prev, next) => {
  if (prev.fieldMode !== next.fieldMode) return false;
  if (prev.enableNumber !== next.enableNumber) return false;
  if (prev.enableMenu !== next.enableMenu) return false;
  if (prev.fontSize !== next.fontSize) return false;
  if (prev.fontColor !== next.fontColor) return false;
  if (prev.fontStyle !== next.fontStyle) return false;
  if (prev.fontWeight !== next.fontWeight) return false;
  if (prev.textAlign !== next.textAlign) return false;
  if (prev.textTransform !== next.textTransform) return false;
  if (prev.textDecoration !== next.textDecoration) return false;
  if (prev.textOverflow !== next.textOverflow) return false;
  if (prev.backgroundColor !== next.backgroundColor) return false;
  if (prev.padding !== next.padding) return false;
  if (prev.borderWidth !== next.borderWidth) return false;
  if (prev.borderColor !== next.borderColor) return false;
  if (prev.rteColumns !== next.rteColumns && !isEqual(prev.rteColumns, next.rteColumns)) return false;
  if (prev.tableColumns !== next.tableColumns && !isEqual(prev.tableColumns, next.tableColumns)) return false;
  return true;
};

export default memo(TableFieldRteHeader, arePropsEqual);
